import classNames from "classnames";
import React, { useState, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import StarekContext from "../../context/StarekContext";

const CycleStageCard = ({
    id,
    expanded = false,
    removeCycleStage,
    updateCycleStage,
    disableDelete = false,
    initialData,
}) => {
    const starekContext = React.useContext(StarekContext);

    const [isExpanded, setIsExpanded] = useState(expanded);
    const [stageName, setStageName] = useState("");
    const [stageDuration, setStageDuration] = useState("");
    const [stageDurationMinutes, setStageDurationMinutes] = useState("");
    const [stageDurationSeconds, setStageDurationSeconds] = useState("");
    const [isProductive, setIsProductive] = useState(null);
    const [errors, setErrors] = useState({});

    const PRODUCTIVE_ID =
        starekContext.types.cycle.cycle_stage_types.PRODUCTIVE;
    const NON_PRODUCTIVE_ID =
        starekContext.types.cycle.cycle_stage_types.NON_PRODUCTIVE;

    const options = useMemo(
        () => [
            { value: PRODUCTIVE_ID, label: "Productive" },
            { value: NON_PRODUCTIVE_ID, label: "Non Productive" },
        ],
        [PRODUCTIVE_ID, NON_PRODUCTIVE_ID]
    );

    useEffect(() => {
        if (!initialData) {
            setStageName("");
            setStageDuration("");
            setStageDurationMinutes("");
            setStageDurationSeconds("");
            setIsProductive(null);
            return;
        }

        const name = initialData.name || "";
        const durationSec = Number(initialData.duration) || 0;
        const mins = durationSec ? Math.floor(durationSec / 60) : "";
        const secs = durationSec ? durationSec % 60 : "";

        setStageName(name);
        setStageDuration(durationSec || "");
        setStageDurationMinutes(mins !== "" ? String(mins) : "");
        setStageDurationSeconds(secs !== "" ? String(secs) : "");
        setIsProductive(
            initialData.is_productive === true
                ? true
                : initialData.is_productive === false
                ? false
                : null
        );
    }, [initialData]);

    useEffect(() => {
        const newErrors = {};
        if (!stageName) newErrors.name = "Stage name is required";
        if (!stageDuration || parseInt(stageDuration) <= 0)
            newErrors.duration = "Duration is required";
        if (isProductive === null) newErrors.type = "Type is required";
        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            updateCycleStage?.(id, {
                id,
                name: stageName,
                duration: parseInt(stageDuration, 10),
                is_productive: isProductive,
            });
        }
    }, [stageName, stageDuration, isProductive, id]);

    const toggleCollapse = () => setIsExpanded((v) => !v);

    const handleCycleStageCardDelete = () => {
        if (!disableDelete) removeCycleStage?.(id);
    };

    const currentTypeOption =
        isProductive === true
            ? options.find((o) => o.value === PRODUCTIVE_ID)
            : isProductive === false
            ? options.find((o) => o.value === NON_PRODUCTIVE_ID)
            : null;

    return (
        <div className="collapsible-container border rounded mb-2">
            <div className="collapsible-header d-flex justify-content-between align-items-center bg-color-e6e6e6 p-2">
                <div>
                    <span
                        className={classNames("rounded-circle px-9px me-2", {
                            "bg-success": isProductive === true,
                            "bg-danger": isProductive === false,
                            "bg-color-a9b0bc": isProductive === null,
                        })}
                    />
                    <span>
                        {stageName || "Name"} |{" "}
                        {stageDuration
                            ? `${Math.floor(stageDuration / 60)}m ${
                                  stageDuration % 60
                              }s`
                            : "Duration"}
                    </span>
                </div>
                <div className="collapsible-controls">
                    {isExpanded && (
                        <i
                            className={classNames("fa fa-trash-alt me-2", {
                                "text-muted": disableDelete,
                                "tw-cursor-pointer": !disableDelete,
                            })}
                            title={
                                disableDelete
                                    ? "At least one stage is required"
                                    : "Delete"
                            }
                            onClick={handleCycleStageCardDelete}
                        />
                    )}
                    <i
                        className={classNames(
                            "cursor-pointer",
                            { "fa fa-chevron-up": isExpanded },
                            { "fa fa-chevron-down": !isExpanded }
                        )}
                        onClick={toggleCollapse}
                    />
                </div>
            </div>

            {isExpanded && (
                <div className="p-3">
                    <Form.Group className="mb-3" controlId={`stage_name_${id}`}>
                        <Form.Label className="font-weight-normal">
                            Stage Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter here"
                            name="stage_name"
                            value={stageName}
                            onChange={(e) => setStageName(e.target.value)}
                        />
                        {errors.name && (
                            <div className="text-danger small">
                                {errors.name}
                            </div>
                        )}
                    </Form.Group>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group
                                className="mb-3"
                                controlId={`stage_duration_${id}`}
                            >
                                <Form.Label className="font-weight-normal">
                                    Duration{" "}
                                    <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        type="number"
                                        placeholder="Minutes"
                                        name="stage_duration_minutes"
                                        min={0}
                                        value={stageDurationMinutes}
                                        onChange={(e) => {
                                            const minutes = parseInt(
                                                e.target.value || 0,
                                                10
                                            );
                                            const seconds = parseInt(
                                                stageDurationSeconds || 0,
                                                10
                                            );
                                            const total =
                                                minutes * 60 + seconds;
                                            setStageDurationMinutes(
                                                e.target.value
                                            );
                                            setStageDuration(
                                                isNaN(total) ? "" : total
                                            );
                                        }}
                                    />
                                    <Form.Control
                                        type="number"
                                        placeholder="Seconds"
                                        name="stage_duration_seconds"
                                        min={0}
                                        max={59}
                                        value={stageDurationSeconds}
                                        onChange={(e) => {
                                            const seconds = parseInt(
                                                e.target.value || 0,
                                                10
                                            );
                                            const minutes = parseInt(
                                                stageDurationMinutes || 0,
                                                10
                                            );
                                            const total =
                                                minutes * 60 + seconds;
                                            setStageDurationSeconds(
                                                e.target.value
                                            );
                                            setStageDuration(
                                                isNaN(total) ? "" : total
                                            );
                                        }}
                                    />
                                </div>
                            </Form.Group>
                            {errors.duration && (
                                <div className="text-danger small">
                                    {errors.duration}
                                </div>
                            )}
                        </div>

                        <div className="col-md-6">
                            <Form.Group
                                className="mb-3"
                                controlId={`stage_type_${id}`}
                            >
                                <Form.Label className="font-weight-normal">
                                    Type <span className="text-danger">*</span>
                                </Form.Label>
                                <Select
                                    id={`stage_type_${id}`}
                                    options={options}
                                    placeholder="Select"
                                    isClearable
                                    aria-label="stage_type"
                                    value={currentTypeOption}
                                    onChange={(selected) => {
                                        if (!selected) {
                                            setIsProductive(null);
                                            return;
                                        }
                                        if (selected.value === PRODUCTIVE_ID)
                                            setIsProductive(true);
                                        else if (
                                            selected.value === NON_PRODUCTIVE_ID
                                        )
                                            setIsProductive(false);
                                    }}
                                />
                            </Form.Group>
                            {errors.type && (
                                <div className="text-danger small">
                                    {errors.type}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CycleStageCard;
